import { useCallback, useEffect, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';
import { SpanishTooltip } from '../../../components/charts/SpanishTooltip';
import {
  fetchSafetyIndices,
  fetchSafetyPeriodHours,
  upsertSafetyPeriodHours,
} from './incidentsApi';
import type { SafetyIndicesResult } from './types';
import { defaultPeriodRange, formatIndex } from './incidentsUtils';
import { usePlatformAccessLevel } from '../../platformPermissions/usePlatformAccessLevel';

export function SafetyIndicesPanel() {
  const { canEdit } = usePlatformAccessLevel('iso45001.incidents.access');
  const [range, setRange] = useState(defaultPeriodRange);
  const [indices, setIndices] = useState<SafetyIndicesResult | null>(null);
  const [hoursForm, setHoursForm] = useState({ ...defaultPeriodRange(), hoursWorked: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadIndices = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchSafetyIndices({
        from: new Date(`${range.from}T00:00:00`).toISOString(),
        to: new Date(`${range.to}T23:59:59`).toISOString(),
      });
      setIndices(result);
    } catch {
      setError('No se pudieron calcular los índices SST.');
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to]);

  useEffect(() => {
    void loadIndices();
    void fetchSafetyPeriodHours().catch(() => undefined);
  }, [loadIndices]);

  const trendData = indices
    ? [
        { name: 'Frecuencia', value: indices.frequencyIndex ?? 0 },
        { name: 'Gravedad', value: indices.severityIndex ?? 0 },
        { name: 'Incidencia', value: indices.incidenceIndex ?? 0 },
      ]
    : [];

  const saveHours = async () => {
    const hours = Number(hoursForm.hoursWorked);
    if (!Number.isFinite(hours) || hours < 0) return;
    await upsertSafetyPeriodHours({
      periodStart: new Date(`${hoursForm.periodStart}T00:00:00`).toISOString(),
      periodEnd: new Date(`${hoursForm.periodEnd}T23:59:59`).toISOString(),
      hoursWorked: hours,
    });
    await loadIndices();
  };

  return (
    <section className="border border-border rounded-lg p-4 space-y-4 bg-card">
      <div className="flex flex-wrap items-end gap-3 justify-between">
        <div>
          <h2 className="font-semibold">Índices SST</h2>
          <p className="text-sm text-muted-foreground">ISO 45001 — frecuencia, gravedad e incidencia</p>
        </div>
        <div className="flex flex-wrap gap-2 items-end">
          <label className="text-sm space-y-1">
            <span>Desde</span>
            <input
              type="date"
              className="input"
              value={range.from}
              onChange={(e) => setRange((prev) => ({ ...prev, from: e.target.value }))}
            />
          </label>
          <label className="text-sm space-y-1">
            <span>Hasta</span>
            <input
              type="date"
              className="input"
              value={range.to}
              onChange={(e) => setRange((prev) => ({ ...prev, to: e.target.value }))}
            />
          </label>
          <button type="button" className="btn btn-outline btn-sm" onClick={() => void loadIndices()}>
            Actualizar
          </button>
        </div>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}
      {loading && <p className="text-sm text-muted-foreground">Calculando…</p>}

      {indices && !loading && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
            <div className="rounded border border-border p-3">
              <p className="text-muted-foreground">Índice de frecuencia</p>
              <p className="text-2xl font-semibold">{formatIndex(indices.frequencyIndex)}</p>
            </div>
            <div className="rounded border border-border p-3">
              <p className="text-muted-foreground">Índice de gravedad</p>
              <p className="text-2xl font-semibold">{formatIndex(indices.severityIndex)}</p>
            </div>
            <div className="rounded border border-border p-3">
              <p className="text-muted-foreground">Índice de incidencia</p>
              <p className="text-2xl font-semibold">{formatIndex(indices.incidenceIndex)}</p>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Horas trabajadas: {indices.hoursWorked} ({indices.hoursWorkedSource})
            {indices.note ? ` — ${indices.note}` : ''}
          </p>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <SpanishTooltip />
                <Bar dataKey="value" name="Índice" fill="#2563eb" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      )}

      {canEdit && (
        <div className="border-t border-border pt-4 space-y-2">
          <h3 className="text-sm font-medium">Registrar horas trabajadas del período</h3>
          <div className="flex flex-wrap gap-2 items-end">
            <input
              type="date"
              className="input"
              value={hoursForm.periodStart}
              onChange={(e) => setHoursForm((p) => ({ ...p, periodStart: e.target.value }))}
            />
            <input
              type="date"
              className="input"
              value={hoursForm.periodEnd}
              onChange={(e) => setHoursForm((p) => ({ ...p, periodEnd: e.target.value }))}
            />
            <input
              type="number"
              min={0}
              className="input w-36"
              placeholder="Horas"
              value={hoursForm.hoursWorked}
              onChange={(e) => setHoursForm((p) => ({ ...p, hoursWorked: e.target.value }))}
            />
            <button type="button" className="btn btn-outline btn-sm" onClick={() => void saveHours()}>
              Guardar horas
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
